import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Calculator,
  TrendingUp,
  AlertOctagon,
  Wallet,
  ArrowRight,
  Sparkles,
  Mail,
  CheckCircle2,
} from "lucide-react";
import { formatBRL } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Industry benchmarks (conservative)
const WASTE_RECOVERY = 0.65; // Ultra recovers 65% of waste loss
const FINES_PREVENTION = 0.8; // prevents 80% of compliance fines
const PLAN_COST_PER_UNIT_MONTH = 399; // Professional plan

export default function ROICalculator() {
  const [revenue, setRevenue] = useState(180000); // R$/mês por unidade
  const [units, setUnits] = useState(2);
  const [wastePct, setWastePct] = useState(4); // %
  const [fineCost, setFineCost] = useState(15000);
  const [finesPerYear, setFinesPerYear] = useState(1.2);

  // Lead capture state
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  const result = useMemo(() => {
    const totalMonthlyRevenue = revenue * units;
    const annualRevenue = totalMonthlyRevenue * 12;
    const annualWasteLoss = annualRevenue * (wastePct / 100);
    const annualFinesLoss = fineCost * finesPerYear * units;
    const totalLoss = annualWasteLoss + annualFinesLoss;

    const wasteRecovered = annualWasteLoss * WASTE_RECOVERY;
    const finesPrevented = annualFinesLoss * FINES_PREVENTION;
    const totalSavings = wasteRecovered + finesPrevented;

    const annualCost = PLAN_COST_PER_UNIT_MONTH * units * 12;
    const netGain = totalSavings - annualCost;
    const roi = annualCost > 0 ? (netGain / annualCost) * 100 : 0;
    const paybackMonths =
      totalSavings > 0 ? (annualCost / (totalSavings / 12)) : 0;

    return {
      totalLoss,
      totalSavings,
      annualCost,
      netGain,
      roi,
      paybackMonths,
      wasteRecovered,
      finesPrevented,
    };
  }, [revenue, units, wastePct, fineCost, finesPerYear]);

  const submitLead = async () => {
    if (!lead.email || !/\S+@\S+\.\S+/.test(lead.email)) {
      toast.error("Email inválido");
      return;
    }
    setLeadSubmitting(true);
    try {
      await api.post("/leads", {
        email: lead.email,
        name: lead.name,
        phone: lead.phone,
        monthly_revenue: revenue * units,
        units,
        estimated_annual_savings: result.totalSavings,
        metadata: {
          waste_pct: wastePct,
          fine_cost: fineCost,
          fines_per_year: finesPerYear,
          net_gain: result.netGain,
          roi_percent: result.roi,
          source_url: window.location.href,
        },
      });
      setLeadSent(true);
      toast.success("Relatório enviado! Confira sua caixa de entrada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <section
      id="roi"
      data-testid="roi-calculator"
      className="py-20 bg-gradient-to-b from-emerald-50/40 to-stone-50 border-y border-stone-200 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">
            <Calculator className="h-3 w-3 mr-1" /> Calculadora
          </Badge>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-stone-950">
            Quanto sua cozinha está perdendo todo mês?
          </h2>
          <p className="mt-3 font-body text-stone-600 max-w-2xl">
            Estime em segundos quanto você recupera em desperdício, multas e
            retrabalho com Ultra. Baseado em benchmarks do setor de food service.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          {/* INPUTS */}
          <Card className="lg:col-span-7 p-6 sm:p-8 rounded-2xl border-stone-200 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
              Sua operação
            </div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
              Conte um pouco sobre sua cozinha
            </h3>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="font-body text-sm">Faturamento mensal por unidade (R$)</Label>
                <Input
                  type="number"
                  min="0"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value) || 0)}
                  className="mt-1.5 bg-stone-50"
                  data-testid="roi-revenue"
                />
              </div>
              <div>
                <Label className="font-body text-sm">Número de unidades operadas</Label>
                <Input
                  type="number"
                  min="1"
                  value={units}
                  onChange={(e) => setUnits(Math.max(1, Number(e.target.value) || 1))}
                  className="mt-1.5 bg-stone-50"
                  data-testid="roi-units"
                />
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="font-body text-sm">% de perda operacional / desperdício</Label>
                  <span className="font-display font-bold text-emerald-800">{wastePct.toFixed(1)}%</span>
                </div>
                <Slider
                  min={0.5}
                  max={12}
                  step={0.1}
                  value={[wastePct]}
                  onValueChange={(v) => setWastePct(v[0])}
                  className="mt-3"
                  data-testid="roi-waste-slider"
                />
                <p className="mt-1.5 text-xs text-stone-500 font-body">
                  Benchmark food service: 4% – 8% do faturamento em desperdício de insumos e retrabalho.
                </p>
              </div>

              <div>
                <Label className="font-body text-sm">Custo médio de uma multa (R$)</Label>
                <Input
                  type="number"
                  min="0"
                  value={fineCost}
                  onChange={(e) => setFineCost(Number(e.target.value) || 0)}
                  className="mt-1.5 bg-stone-50"
                  data-testid="roi-fine-cost"
                />
                <p className="mt-1.5 text-xs text-stone-500 font-body">ANVISA + Vigilância Sanitária.</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="font-body text-sm">Multas por unidade/ano</Label>
                  <span className="font-display font-bold text-emerald-800">{finesPerYear.toFixed(1)}</span>
                </div>
                <Slider
                  min={0}
                  max={6}
                  step={0.1}
                  value={[finesPerYear]}
                  onValueChange={(v) => setFinesPerYear(v[0])}
                  className="mt-3"
                  data-testid="roi-fines-slider"
                />
              </div>
            </div>
          </Card>

          {/* RESULTS */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Card className="p-6 rounded-2xl border-stone-200 bg-stone-950 text-white relative overflow-hidden">
              <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl" />
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400 font-body">
                  Economia projetada
                </div>
                <div
                  className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tight"
                  data-testid="roi-savings"
                >
                  {formatBRL(result.totalSavings)}
                </div>
                <div className="text-xs text-stone-400 font-body mt-1">por ano · {units} unidade{units > 1 ? "s" : ""}</div>

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm font-body">
                  <div className="bg-stone-900/70 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400">ROI anual</div>
                    <div className="mt-1 font-display font-bold text-emerald-400 text-2xl" data-testid="roi-percent">
                      {result.roi.toFixed(0)}%
                    </div>
                  </div>
                  <div className="bg-stone-900/70 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400">Payback</div>
                    <div className="mt-1 font-display font-bold text-emerald-400 text-2xl" data-testid="roi-payback">
                      {result.paybackMonths > 0 && Number.isFinite(result.paybackMonths)
                        ? `${result.paybackMonths.toFixed(1)}m`
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-5 rounded-2xl border-stone-200 bg-white">
              <div className="space-y-3 text-sm font-body">
                <Row
                  icon={AlertOctagon}
                  iconClass="bg-rose-100 text-rose-700"
                  label="Perda anual estimada (hoje)"
                  value={formatBRL(result.totalLoss)}
                  testId="roi-loss"
                />
                <Row
                  icon={TrendingUp}
                  iconClass="bg-emerald-100 text-emerald-700"
                  label="Desperdício recuperado (65%)"
                  value={formatBRL(result.wasteRecovered)}
                />
                <Row
                  icon={TrendingUp}
                  iconClass="bg-emerald-100 text-emerald-700"
                  label="Multas evitadas (80%)"
                  value={formatBRL(result.finesPrevented)}
                />
                <Row
                  icon={Wallet}
                  iconClass="bg-stone-100 text-stone-700"
                  label={`Custo Ultra (${units} un. · plano Pro)`}
                  value={`- ${formatBRL(result.annualCost)}`}
                  muted
                />
                <div className="pt-3 border-t border-stone-200 flex items-center justify-between">
                  <div className="text-stone-700 font-semibold">Ganho líquido / ano</div>
                  <div
                    className={`font-display text-xl font-extrabold ${result.netGain >= 0 ? "text-emerald-700" : "text-rose-600"}`}
                    data-testid="roi-net-gain"
                  >
                    {formatBRL(result.netGain)}
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setLeadOpen(true)}
                className="flex-1 bg-emerald-800 hover:bg-emerald-900 text-white h-11 font-body"
                data-testid="roi-cta-email"
              >
                <Mail className="h-4 w-4 mr-2" /> Receber relatório no email
              </Button>
              <Link to="/register">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-stone-300 bg-white hover:bg-stone-100 h-11 font-body"
                  data-testid="roi-cta-trial"
                >
                  <Sparkles className="h-4 w-4 mr-2" /> Iniciar trial
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="ghost"
                  className="w-full sm:w-auto h-11 font-body text-stone-700 hover:text-stone-900"
                  data-testid="roi-cta-plans"
                >
                  Ver planos <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <p className="text-[11px] text-stone-500 font-body leading-relaxed">
              Estimativa baseada em benchmarks de redes de food service (recuperação média de 60–70% no desperdício
              operacional e 75–85% de redução em ocorrências sanitárias após digitalização de checklists e APPCC). Resultados
              individuais variam.
            </p>
          </div>
        </div>
      </div>

      {/* Lead capture modal */}
      <Dialog
        open={leadOpen}
        onOpenChange={(open) => {
          setLeadOpen(open);
          if (!open) {
            setLeadSent(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          {!leadSent ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Receba seu relatório personalizado
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm font-body text-emerald-900">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4" />
                    Economia projetada anual
                  </div>
                  <div className="font-display text-2xl font-extrabold mt-1">
                    {formatBRL(result.totalSavings)}
                  </div>
                  <div className="text-xs text-emerald-800/80 mt-0.5">
                    {units} unidade{units > 1 ? "s" : ""} · ROI {result.roi.toFixed(0)}%
                  </div>
                </div>

                <div>
                  <Label className="font-body text-sm">Nome</Label>
                  <Input
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    placeholder="Seu nome completo"
                    className="mt-1 bg-white"
                    data-testid="roi-lead-name"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">E-mail corporativo *</Label>
                  <Input
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    placeholder="voce@suacozinha.com.br"
                    required
                    className="mt-1 bg-white"
                    data-testid="roi-lead-email"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Celular (opcional)</Label>
                  <PhoneInput
                    value={lead.phone}
                    onChange={(v) => setLead({ ...lead, phone: v })}
                    className="mt-1"
                    data-testid="roi-lead-phone"
                  />
                </div>
                <p className="text-[11px] text-stone-500 font-body">
                  Enviamos seu cálculo personalizado, comparativos do setor e cases de redes parceiras. Nada de spam.
                </p>
              </div>
              <DialogFooter>
                <Button
                  onClick={submitLead}
                  disabled={leadSubmitting}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white font-body w-full sm:w-auto"
                  data-testid="roi-lead-submit"
                >
                  {leadSubmitting ? "Enviando..." : "Enviar relatório"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-700" />
              </div>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight">
                Pronto, {lead.name?.split(" ")[0] || "tudo certo"}!
              </h3>
              <p className="mt-2 font-body text-sm text-stone-600">
                Enviamos uma cópia do seu cálculo para <strong>{lead.email}</strong>. Em até 24h um consultor entra em contato
                para mostrar como a Ultra captura essa economia na sua cozinha.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                <Link to="/register">
                  <Button className="bg-emerald-800 hover:bg-emerald-900 text-white font-body" data-testid="roi-lead-trial">
                    Quero começar trial agora <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => {
                    setLeadOpen(false);
                    setLeadSent(false);
                  }}
                  className="font-body"
                >
                  Continuar explorando
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Row({ icon: Icon, iconClass, label, value, muted, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${iconClass}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 text-stone-700">{label}</div>
      <div className={`font-display font-bold ${muted ? "text-stone-500" : "text-stone-950"}`}>{value}</div>
    </div>
  );
}
